// oxlint-disable zeffy-linter-frontend/avoid-vh -- random Cloudinary asset hashes can contain the letters 'vh'
import { RegisterCategory } from "@/features/Auth/Register/types";

export type ShowcaseStep = "one" | "two" | "three" | "four" | "five";

export interface ShowcaseReview {
  quote: string;
  name: string;
  organization?: string;
  avatarUrl: string;
}

export interface ShowcaseStepContent {
  headline: string;
  subtitle: string;
  visualUrl: string;
}

export interface ShowcaseSolutionContent {
  backgroundUrl: string;
  steps: Record<Exclude<ShowcaseStep, "five">, ShowcaseStepContent>;
  reviewsHeadline: string;
  reviews: ShowcaseReview[];
}

export const SHOWCASE_CONTENT: Partial<Record<RegisterCategory, ShowcaseSolutionContent>> = {
  donation: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747443/wysiwyg/pavzkma2rvazzge4ktma.jpg",
    steps: {
      one: {
        headline: "Creating your donation form\nis easier than ever",
        subtitle:
          "Add your colors, logo, and images. Suggest amounts or let donors choose. Accept one-time and recurring gifts, then share on your form online.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750199/wysiwyg/sp4fo3yzw6knx0jxlucp.png",
      },
      two: {
        headline: "Track every donor in\none place",
        subtitle:
          "Every donation is associated to the right donor, keeping their giving history in one place. Turn on tax receipts and they go out the moment a gift comes in.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785758259/wysiwyg/rv4daw58b4bnjhk0llsc.png",
      },
      three: {
        headline: "Keep 100% of every donation",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750201/wysiwyg/upxs6pu2rxktr823j7ch.png",
      },
      four: {
        headline: "Keep 100% of every donation",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750194/wysiwyg/obt0kspxg2g61x3mz0l7.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“With Zeffy, we receive 100% of every donation, which makes an enormous difference in our ability to fund programs and support those in crisis.”",
        name: "Kim B.",
        organization: "The Brandon Caserta Foundation",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747511/wysiwyg/i5p3x1pk7gfsvkltd6vg.png",
      },
      {
        quote:
          "“We keep 100% of every donation or sale, no transaction fees, which has saved us $2,212 in just one year.”",
        name: "Morgan H.",
        organization: "Hartland Lakeside Education Foundation",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747512/wysiwyg/mubtpwynvutdql3gbooh.jpg",
      },
      {
        quote:
          "“I find Zeffy incredibly helpful for end-of-year tax donations, sending thank you emails, and making it easy for our repeated donors to set up recurring donations.”",
        name: "Carmen C.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747578/wysiwyg/yic98bhanzxrohyqrvi7.jpg",
      },
      {
        quote: "“The credit card fees would eat up all of my donations. Zeffy solved that problem 100%.”",
        name: "Stephani P.",
        organization: "Courtroom Critters",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747579/wysiwyg/dggz20emointkpq2rsz2.jpg",
      },
      {
        quote:
          "“Zeffy is a really good, simple platform for online giving, so I can set it up and share a link or QR code to include in any of our materials or online platforms.”",
        name: "Michael S.",
        organization: "Nehemiah Community Development",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747580/wysiwyg/ikvmerclsbwm0mnazs8j.jpg",
      },
      {
        quote:
          "“The platform is easy to use, offers customizable forms, and automates tax receipts, which saves us time and administrative work.”",
        name: "AC",
        organization: "The Maria F. Ganong Seniors Residence Inc.",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747581/wysiwyg/x64j7fqlgat4u3d5nbpf.jpg",
      },
      {
        quote:
          "“Zeffy has taken all the hard work out so we can focus on helping the ones we serve more instead of chasing those donation receipts.”",
        name: "Anginette E.",
        organization: "Disabled American Veterans",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747584/wysiwyg/wlbxcndpvqutx8njeyu1.jpg",
      },
      {
        quote:
          "“Zeffy helps us track donations and make sure receipts are issued when appropriate. It also lets us stay connected with our supporters through mailouts and updates”",
        name: "Tanya F.",
        organization: "Streams Community Hub",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747585/wysiwyg/p6hwyj1cjnjnuhp4dpsh.jpg",
      },
    ],
  },
  event: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747498/wysiwyg/ea5w4613ajypymmburig.png",
    steps: {
      one: {
        headline: "Creating your ticketing form\nis easier than ever.",
        subtitle:
          "Personalize it with your colors, logo, and images. Add as many ticket types and rates as your event needs, then share it online with your supporters.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750272/wysiwyg/cta6exrgg6xijlukinoe.png",
      },
      two: {
        headline: "Manage your guest list\nand check people in",
        subtitle:
          "Track participants and access your guest list ahead of time. On event day, scan QR codes to check people in and take card payments with Tap to Pay.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785767404/wysiwyg/vufwalgucuuy0tlwgxff.png",
      },
      three: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750274/wysiwyg/fiddyiglky3cougo5itq.png",
      },
      four: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750269/wysiwyg/olneytzuvua8u8hy4jq4.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“We had used Eventbrite for our Concert ticket sales and hated the fact that they were taking 20% of our sales. with Zeffy we saved $600 on our last concert.”",
        name: "Jim C.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748172/wysiwyg/ynvwgfubv3d2chwxezeu.jpg",
      },
      {
        quote:
          "“Zeffy has already saved us thousands on tickets for our Walk to End Parkinson's and our upcoming Red Tulip Gala.”",
        name: "Erika G.",
        organization: "Director of Development",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748175/wysiwyg/xteuzksnvaaktr8fanxq.jpg",
      },
      {
        quote:
          "“Setting up our first gala ticketing page was impressively easy and professional-looking, going live in under 20 minutes without needing a developer.”",
        name: "Jayesh M.",
        organization: "Program Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748178/wysiwyg/tmvseier6qxt7w960ega.jpg",
      },
      {
        quote: "“The event features of guest check-in, e-tickets and email capabilities is saving me so much time.”",
        name: "Courtney H.",
        organization: "Director of Development",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748225/wysiwyg/ybjolgioivmvfoakodtw.jpg",
      },
      {
        quote:
          "“We used to do all of our ticketing through another well-known site and it was so clunky. Zeffy is much easier to use, so we use it a lot!”",
        name: "Beth D.",
        organization: "Les Dames d'Escoffier",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748229/wysiwyg/yv9w9hbcvl5xuuiwbxpw.jpg",
      },
      {
        quote:
          "“Zeffy ticketing is so easy for our audience members to use. Even up to the last minute purchasing tickets.”",
        name: "Laura M.",
        organization: "Executive Sponsor",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748231/wysiwyg/ipysucr5mq0x8rhetd2u.jpg",
      },
      {
        quote:
          "“Because we save on each ticket sale and donation, we are able to keep that money in the nonprofit to fund even more dance scholarships”",
        name: "Michelle C.",
        organization: "Project SMASH",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748233/wysiwyg/hqjpx0uirrqcgqeabsbb.jpg",
      },
      {
        quote: "“We are able to avoid charges for large event registration when we use Zeffy over eventbrite.”",
        name: "Dr. Nelson M.",
        organization: "The Center, One Foundation",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748234/wysiwyg/agshefk77z2tdhddfjo9.jpg",
      },
    ],
  },
  raffle: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747479/wysiwyg/d1w3krzlf5ijlwk3baxg.png",
    steps: {
      one: {
        headline: "Running your raffle is easier\nthan ever.",
        subtitle:
          "Add your logo, colors, and images, then set ticket bundles for your participants to pick. Then share your form online with your supporters.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750217/wysiwyg/oypt3a7puosibi17f7vy.png",
      },
      two: {
        headline: "Track participants and draw\nyour winner",
        subtitle:
          "Every ticket has a unique number, and buyers land on your participant list. Export reports in one click, then use a draw tool to pick your winner.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750220/wysiwyg/mzxncp3lkuegnuhwjbl1.png",
      },
      three: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750219/wysiwyg/qfskogpangtzlj7v9l4q.png",
      },
      four: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750214/wysiwyg/hqvl4g76bgabbr4qpuut.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“Before with our raffles we had to collect the money through PayPal and then email the ticket numbers to the individuals. Now that's all done by Zeffy and with no fees.”",
        name: "Barbara B.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747708/wysiwyg/fbawkfme8do5upql20sq.jpg",
      },
      {
        quote:
          "“Zeffy is very easy for customers to make their purchase for raffle tickets. Zeffy helps us save money by not charging any processing fees for credit cards”",
        name: "Jeff S.",
        organization: "Development Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747711/wysiwyg/hkohey0ssltvmqlbvmgg.jpg",
      },
      {
        quote: "“We used the raffle portion and it went great. It was very easy to use and quick set up.”",
        name: "Eric M.",
        organization: "Program Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747713/wysiwyg/beaylku1vpeav2gp3rhh.jpg",
      },
      {
        quote:
          "“The reporting available allowed me to pull in the name, address, phone and ticket number into excel to mail merge to print out tickets for the drawing. It was SO EASY!”",
        name: "Todd H.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747781/wysiwyg/buu8g2fgzznsnvhe6kxb.jpg",
      },
      {
        quote:
          "“We were able to sell tickets to our charity event, host a wine wall, and sell raffle tickets all from one site.”",
        name: "Victoria J.",
        organization: "Development Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747840/wysiwyg/hudejxubvb4ekdl9e4jl.jpg",
      },
      {
        quote:
          "“It has been wonderful for selling tickets to events, managing registrations, and even running an online raffle. We have significantly increased our fundraising now that we can accept online payments without fees.”",
        name: "Martin B.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747843/wysiwyg/buorjnstfz4n13enowev.jpg",
      },
      {
        quote:
          "“With the ability to send thousands of emails and sell thousands of raffle tickets through the platform with zero credit cards fees is going to be amazing!”",
        name: "Shadley S.",
        organization: "Director of Donor Relations",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747845/wysiwyg/k6y0cg6iawavmogfetfj.jpg",
      },
      {
        quote:
          "“Zeffy was super easy to update with content and pictures of items that were part of my raffle fundraiser.”",
        name: "Jessica C.",
        organization: "TSC Alliance",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747849/wysiwyg/zty4rcncdzlvm1q6zpon.jpg",
      },
    ],
  },
  "peer-to-peer": {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747460/wysiwyg/gmrwveetl304tsx53pn7.jpg",
    steps: {
      one: {
        headline: "Launching your campaign\nis easier than ever.",
        subtitle:
          "Brand your peer-to-peer campaign with your colors, logo, and images, then let supporters join as individuals or teams and build their own fundraising pages.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750209/wysiwyg/hfmyu8vhvs8dsjrf6pv2.png",
      },
      two: {
        headline: "Spark friendly competition\nwith a leaderboard",
        subtitle:
          "Your leaderboard ranks each team and fundraiser by how much they've raised, updating in real time as new donations arrive.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785758260/wysiwyg/lscuxkdarwztyldtbo79.png",
      },
      three: {
        headline: "Keep 100% of every donation",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750211/wysiwyg/n8hkhcjxencubhrrf7j7.png",
      },
      four: {
        headline: "Keep 100% of every donation",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750204/wysiwyg/b11yqkiy7mmsaqesgnar.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“Zeffy helps us support our athletes in raising money to offset the cost of participation without hidden fees. Each participant receives exactly what has been donated to them.”",
        name: "Lorie K.",
        organization: "Rainier Volleyball Club",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747646/wysiwyg/scjiznwucleszavormlo.jpg",
      },
      {
        quote:
          "“Our previous fundraising CRM charged for such things as P2P fundraisers. Zeffy allows us to carry out these types of fundraisers for no cost.”",
        name: "Sara P.",
        organization: "Empower Missouri",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747648/wysiwyg/jh9wlyycov2bv9odlsaw.jpg",
      },
      {
        quote:
          "“Zeffy offers a peer to peer fundraising option that's free and works very well. Others charge hundreds of dollars for the same service!”",
        name: "Theresa W.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747650/wysiwyg/s6genrvwfuimyur8rejo.jpg",
      },
      {
        quote:
          "“Zeffy is incredibly intuitive — it’s easy to create and manage campaigns, peer-to-peer fundraisers, and even virtual silent auctions.”",
        name: "Elisa G.",
        organization: "Development Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747697/wysiwyg/lpfgz9sqyonxemmvm2ke.jpg",
      },
      {
        quote:
          "“I love using Zeffy for fundraising as it allows us to easily and effectively collect peer-to-peer funds. The lack of processing fees is fantastic!”",
        name: "Erica J.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747699/wysiwyg/qtrughhaf5fcmppu5anu.jpg",
      },
      {
        quote:
          "“Zeffy is easy to use and customizable without being overwhelming, which allows me to create different peer-to-peer fundraisers for different campaigns.”",
        name: "Liz C.",
        organization: "Founder",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747701/wysiwyg/kgfxrxfsbbj40tas8kdp.jpg",
      },
      {
        quote:
          "“The Zeffy interface is easy to use and delivers attractive materials for social media and email. We used it for event registration, peer-to-peer campaigns, and donations.”",
        name: "Michael L.",
        organization: "Capterra",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747704/wysiwyg/txsbkw8mdpj075upd4no.jpg",
      },
      {
        quote:
          "“I love that Zeffy offers an integrated platform with multiple online solutions whether it be a donation campaign, peer-to-peer fundraising, an auction, an online store and more.”",
        name: "Doug L.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747706/wysiwyg/ysnjci0b87f66cpsfdvt.jpg",
      },
    ],
  },
  membership: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750233/wysiwyg/sqefjfl0uznd9dln2doh.jpg",
    steps: {
      one: {
        headline: "Create and share your membership form",
        subtitle:
          "Add your logo, colors, and a custom banner, then set up multiple membership tiers with their own rates.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750239/wysiwyg/xpzuhrgjiq4xzfrwuphu.png",
      },
      two: {
        headline: "Send digital cards and let members auto-renew",
        subtitle:
          "Automatically send members their digital card. They can opt in to auto-renewal. An automatic reminder is always sent before expiration.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785758262/wysiwyg/ag8yrpfhvrsmkcihpvkw.png",
      },
      three: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750241/wysiwyg/t3d6gi3gqnm6jwv1f9qd.png",
      },
      four: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750234/wysiwyg/c7lbgenzdghan3l7me7n.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“Zeffy allows us to contact donors for membership renewal, email them with ease, sell tickets for events, and it saves us money”",
        name: "Amy P.",
        organization: "Art Reach of Mid Michigan",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747851/wysiwyg/b9rosfzdq9zasranx0mi.jpg",
      },
      {
        quote:
          "“No platform fees and no credit card processing fees charged to nonprofits, which allows organizations to keep 100% of membership dues and event revenue”",
        name: "Administrator",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747853/wysiwyg/nxfrx9idbf9umovu7phf.jpg",
      },
      {
        quote:
          "“The initial setup was very easy with the wizard setup for membership, which was quick to run and easy to edit or update.”",
        name: "Nicholas M.",
        organization: "Program Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747855/wysiwyg/rulzbp65e5fiymgenbz9.jpg",
      },
      {
        quote:
          "“Zeffy allows us to accept online payments, automate our membership renewal, recruit and coordinate volunteers, and centralize membership record keeping!”",
        name: "Jerry T.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747861/wysiwyg/dkohwzlrdf0nk3fthuwz.jpg",
      },
      {
        quote:
          "“We use Zeffy for our membership dues along with any donations and events we have. We have saved thousands of dollars!”",
        name: "Nikki R.",
        organization: "Development Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785747926/wysiwyg/jlny1nf5ibqc1uvxg5bf.jpg",
      },
      {
        quote:
          "“With Zeffy, we are now getting 100% of donations, event fees, and membership fees. They have saved us almost $2000 in credit card fees in the first year.”",
        name: "Lori D.",
        organization: "Marin History Museum",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748037/wysiwyg/ecv7dsiuj7nw9pnvuck5.jpg",
      },
      {
        quote:
          "“The ease of use in setting up membership and donation campaigns is fantastic. Zeffy is always looking for ways to add more value for nonprofits.”",
        name: "Margie C.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748040/wysiwyg/nljj5if8tt5lbqo0scky.jpg",
      },
      {
        quote:
          "“From our membership, to fundraising campaigns and trainings, we are able to have multiple campaigns at once that are easy to use, eye catching, and stress free.”",
        name: "Cassie J.",
        organization: "Black Women DO Heal",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748042/wysiwyg/nlctykm28jby6wdmqmlp.jpg",
      },
    ],
  },
  auction: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750252/wysiwyg/lz80wwayqdllgixubibm.png",
    steps: {
      one: {
        headline: "Hosting your auction is easier\nthan ever.",
        subtitle:
          "Add each item with a starting bid, brand the page with your logo, colors, and images, then share it online with your supporters.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750259/wysiwyg/tzaajwr3j5ulpkxb8ud5.png",
      },
      two: {
        headline: "Let your participants bid\ntheir way",
        subtitle:
          "Bidders can bid across several items, overbid manually or set a maximum amount for auto-bidding. When outbid, they get notified automatically.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785758265/wysiwyg/etgr5cdnehwh5nf73osg.png",
      },
      three: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750261/wysiwyg/wt8q3d9wcdtxhgxputmi.png",
      },
      four: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750255/wysiwyg/nqymxnlif0xsffeedd4g.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote:
          "“We recently used Zeffy for our Rotary Club's online auction, and the experience was nothing short of exceptional!”",
        name: "Julie S.",
        organization: "Rotary Club of District 5010",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748045/wysiwyg/kruhagc8m44tekplfdpx.jpg",
      },
      {
        quote: "“I set up a 13 item virtual auction that was easy at both the back end and the user end.”",
        name: "Cathy W.",
        organization: "Program Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748046/wysiwyg/csyx8nksvfjdmitc0bjh.jpg",
      },
      {
        quote:
          "“We have used Zeffy for donations, as well as an online auction and an in-person event, in the last year or so and have already saved hundreds of dollars in fees!”",
        name: "Tara K.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748049/wysiwyg/n69jb5mttiwzhvxymjjg.jpg",
      },
      {
        quote:
          "“The event ticketing and silent auction features simplify management and consolidate payments and donations in one site.”",
        name: "Jason K.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748110/wysiwyg/yqt6pn1wgxhkfln36thg.jpg",
      },
      {
        quote:
          "“Creating events, managing sponsorships, and organizing auctions are all easy and streamlined. This saves us time, reduces stress, and allows us to focus on our mission”",
        name: "Kim B.",
        organization: "The Brandon Caserta Foundation",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748112/wysiwyg/addjlvom5itopikprcz9.png",
      },
      {
        quote:
          "“Zeffy is super easy to set up, especially for silent auctions and small nonprofits looking to host their first event.”",
        name: "Alyssa A.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748113/wysiwyg/yfjfuipazphzzao8ydjw.jpg",
      },
      {
        quote:
          "“Zeffy’s free platform has saved us a lot of money that can be directed towards supporting our mission instead of escalating fees.”",
        name: "Dallas T.",
        organization: "Saskatchewan Co-operative Association",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748116/wysiwyg/mptg2u5ag5dhnkwsqx29.jpg",
      },
      {
        quote:
          "“Zeffy is 100% free for nonprofits, so every dollar goes to the mission, and it’s easy for both organizers and donors to use.”",
        name: "Michael V.",
        organization: "Thames River Community Service",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748118/wysiwyg/c5xtslo6p9em6gjkblcv.jpg",
      },
    ],
  },
  shop: {
    backgroundUrl:
      "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750290/wysiwyg/jgzq8flltavrsueg6vlg.png",
    steps: {
      one: {
        headline: "Building your online store\nis easier than ever.",
        subtitle:
          "List every product with its own image, price, and description. Share your shop on your website, social media or by email.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750298/wysiwyg/mngtrqerabz01y29bo71.png",
      },
      two: {
        headline: "Tailor each product\nto your buyers",
        subtitle:
          "Add size and color choices to any product, show a photo for each one, and ask a custom question per item to personalize the buyer’s experience.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750304/wysiwyg/jxm3n84glxstbkveoxef.png",
      },
      three: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750301/wysiwyg/omcge88s6nux9y1itvjj.png",
      },
      four: {
        headline: "Keep 100% of the money",
        subtitle:
          "No platform fees. No transaction fees. No credit card fees, ever. Zeffy is funded by optional tips from your donors, so every dollar goes to your mission.",
        visualUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785750293/wysiwyg/c5xjwypstm8ufd1xhysj.png",
      },
    },
    reviewsHeadline: "100K+ nonprofits already\ntrust Zeffy",
    reviews: [
      {
        quote: "“Zeffy gave me a very easy way to set up an online store to fundraise through selling merchandise.”",
        name: "Rylee R.",
        organization: "United Ways of Alabama",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748236/wysiwyg/cf4zywkzep1sbjdy0zxy.jpg",
      },
      {
        quote:
          "“Zeffy makes it easy to create ways for the public to support us from selling tickets to creating an online store. It has been very easy to use and saved us thousands in fees.”",
        name: "Gerald A.",
        organization: "PRISM",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748237/wysiwyg/pg9yzcrdlusogufzawil.jpg",
      },
      {
        quote:
          "“Setting up forms for campaigns, events, and merchandise sales on Zeffy is quick and simple, nothing is complicated, and I appreciate that.”",
        name: "Maggie C.",
        organization: "Executive Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748239/wysiwyg/oxphonr1xb5flypa563s.jpg",
      },
      {
        quote:
          "“Zeffy is a wonderful free platform for nonprofits! We can write a great campaign, add photos and video, offer perks like shirts, and use Zeffy anywhere!”",
        name: "Brian M.",
        organization: "Development Director",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748243/wysiwyg/ljuv0mfabb73q0ismtwv.jpg",
      },
      {
        quote: "“Zeffy does not charge any payment fees, which is a huge plus for my nonprofit organization.”",
        name: "Jeanne K.",
        organization: "Alaska Arts Education Consortium",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748347/wysiwyg/fv5idaclslrez5fxbryy.jpg",
      },
      {
        quote:
          "“I love that Zeffy offers a variety of tools and is 100% free. It's user-friendly and flexible, providing a great amount of features.”",
        name: "Board President D.",
        organization: "Board president",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748349/wysiwyg/d3tymq5kaxg8m21hlgcn.jpg",
      },
      {
        quote:
          "“One of the best features of Zeffy is that it is 100% free to use. Your non-profit will receive every penny that is raised.”",
        name: "Matt V.",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748350/wysiwyg/lf5em9c162dxozxtfefc.jpg",
      },
      {
        quote: "“No fees for payment processing! 100% of funds raised go directly to our school.”",
        name: "WCCA O.",
        avatarUrl:
          "https://res.cloudinary.com/hxn9dbuhd/image/upload/f_auto,q_auto/v1785748351/wysiwyg/ngg5t2civaa6fkyhzsje.jpg",
      },
    ],
  },
};
